// src/config/featureCapabilities.ts
//
// UI Feature → Capability Mapping
//
// ARCHITECTURE: This is a UI-only convenience map.
// It does NOT define permissions - Central owns the capability registry.
// It maps UI features to their required { domain, action } pairs
// for consistent capability checking across the application.
//
// Usage:
//   import { FEATURE_CAPABILITIES } from './featureCapabilities';
//   import { useHasCapability } from '../services/centralContext';
//
//   const canCreate = useHasCapability(
//     FEATURE_CAPABILITIES.createContact.domain,
//     FEATURE_CAPABILITIES.createContact.action
//   );

export interface CapabilityRequirement {
  domain: string;
  action: string;
}

/**
 * Feature capability map.
 * Maps UI features to their required capability pair.
 *
 * IMPORTANT: This is for UI gating only.
 * The server enforces these capabilities via Central.
 */
export const FEATURE_CAPABILITIES = {
  // ============================================================
  // Contacts
  // ============================================================
  viewContacts: { domain: 'contacts', action: 'read' },
  createContact: { domain: 'contacts', action: 'write' },
  editContact: { domain: 'contacts', action: 'write' },
  deleteContact: { domain: 'contacts', action: 'delete' },
  exportContacts: { domain: 'contacts', action: 'export' },
  importContacts: { domain: 'contacts', action: 'import' },
  // Outbound contact-workspace ACTIONS — run research / LinkedIn research
  // (spend credits), save a call note, record an outcome, schedule a callback.
  // The server gates these POST proxies on `contacts.update`, whose role set is
  // identical to Central's `contacts.write` (viewer holds neither). Reading
  // the workspace stays on viewContacts.
  runOutboundContactActions: { domain: 'contacts', action: 'write' },

  // ============================================================
  // Companies
  // ============================================================
  viewCompanies: { domain: 'companies', action: 'read' },
  createCompany: { domain: 'companies', action: 'write' },
  editCompany: { domain: 'companies', action: 'write' },
  deleteCompany: { domain: 'companies', action: 'delete' },

  // ============================================================
  // Forms — C4.4
  //
  // Central's canonical contract publishes NO forms.write and NO
  // forms.delete: mutating an existing form is `manage`, and creating one is
  // `create`. A member holds create=true and manage=FALSE, so the two must
  // never be aliased here.
  //
  // Reading SUBMISSION DATA is a DIFFERENT Central domain. A viewer holds
  // forms.read=true and form_submissions.read=FALSE, which is exactly why the
  // submissions control needs its own entry rather than reusing viewForms.
  // ============================================================
  viewForms: { domain: 'forms', action: 'read' },
  createForm: { domain: 'forms', action: 'create' },
  manageForm: { domain: 'forms', action: 'manage' },
  viewFormSubmissions: { domain: 'form_submissions', action: 'read' },

  // ============================================================
  // Deals / Opportunities
  // ============================================================
  viewDeals: { domain: 'deals', action: 'read' },

  // Managing the PIPELINE ITSELF — create/rename/delete a pipeline or its
  // stages — is `deals:admin`, not `deals:write`. See routes/pipelines.ts:
  // every pipeline mutation carries requireCapabilityMiddleware('deals',
  // 'admin'). Editing a DEAL is a different, lower bar (createDeal below).
  //
  // Named here because the UI was offering "New Pipeline" to everyone who
  // could see the page, and a member got a 403 the interface never mentioned.
  //
  // NOT SUFFICIENT ON ITS OWN. routes/pipelines.ts also applies
  // `requirePermission('pipelines.*')`, i.e. workspace role owner/admin, and
  // applies it FIRST. Central's editor lets an owner grant `crm:deals:admin`
  // to a member, who then passes this capability and is refused by the role
  // floor with INSUFFICIENT_ROLE. Any control keyed on this entry must pair it
  // with `useHasRole(['owner', 'admin'])` — see PipelinePage.
  managePipelines: { domain: 'deals', action: 'admin' },
  createDeal: { domain: 'deals', action: 'write' },
  editDeal: { domain: 'deals', action: 'write' },
  deleteDeal: { domain: 'deals', action: 'delete' },

  // ============================================================
  // Projects
  // ============================================================
  // Mirrors the server's `projects.*` entries in
  // `config/authorizationMatrix.ts`, which is the authority. `create` and
  // `edit` share the `write` action, matching the Contacts/Companies/Deals
  // convention above — the server still distinguishes projects.create from
  // projects.update, so a UI that shows one does not imply the other is
  // permitted server-side.
  viewProjects: { domain: 'projects', action: 'read' },
  createProject: { domain: 'projects', action: 'write' },
  editProject: { domain: 'projects', action: 'write' },
  deleteProject: { domain: 'projects', action: 'delete' },

  // ============================================================
  // Tasks
  // ============================================================
  viewTasks: { domain: 'tasks', action: 'read' },
  createTask: { domain: 'tasks', action: 'write' },
  editTask: { domain: 'tasks', action: 'write' },
  deleteTask: { domain: 'tasks', action: 'delete' },

  // ============================================================
  // Files — C4.5 R3
  //
  // CREATING a file is a WORKSPACE-level authority question, so it is
  // answered by the global capability. It must NOT be derived from the
  // per-resource `file.access` flags: those describe an EXISTING file, and a
  // new upload has no file to describe. A viewer holds files.read=true and
  // files.write=FALSE, which is exactly the case this must deny.
  //
  // Per-file edit/delete/share affordances stay on `file.access`, which is
  // capability AND ordinary record authority combined server-side.
  // ============================================================
  uploadFile: { domain: 'files', action: 'write' },

  // ============================================================
  // Messages — the Contact communications inbox (Unibox)
  // ============================================================
  // Central publishes `crm.messages` with read | write | admin ONLY. There is
  // deliberately no `delete`, and no `send`/`create`/`update` — so every
  // member-safe mutation on this surface collapses to `write`, matching the
  // Contacts/Companies/Projects convention.
  //
  // The collapse is lossless against the server: CRM's backend guards these
  // routes with `messages.send` and `threads.manage`, and both carry the
  // identical role set {owner, admin, member} — which is exactly the set
  // Central grants `messages.write`. `messages.read` matches the backend's
  // `messages.read` ({owner, admin, member, viewer}).
  //
  //   backend messages.read   -> crm.messages.read
  //   backend messages.send   -> crm.messages.write
  //   backend threads.manage  -> crm.messages.write
  //
  // `messages.admin` exists in the payload (owner-only) but nothing in this
  // surface needs it today, so it is intentionally not mapped. Mapping an
  // unused action would invite gating a control on owner-only authority the
  // backend does not actually require.
  viewMessages: { domain: 'messages', action: 'read' },
  sendMessage: { domain: 'messages', action: 'write' },
  createThread: { domain: 'messages', action: 'write' },
  editThread: { domain: 'messages', action: 'write' },
  manageThreadParticipants: { domain: 'messages', action: 'write' },

  // ============================================================
  // Conversations — AI ASSISTANT sessions. NOT the Unibox.
  // ============================================================
  // `crm.conversations` is a separate Central domain backing
  // ConversationSession/ConversationMessage (sender types include
  // `assistant`). It must never be used to gate Contact messaging; doing so
  // was the original source of the "Messages doesn't work" confusion.
  viewConversations: { domain: 'conversations', action: 'read' },
  deleteConversation: { domain: 'conversations', action: 'delete' },

  // ============================================================
  // SMS
  // ============================================================
  viewSms: { domain: 'sms', action: 'read' },
  sendSms: { domain: 'sms', action: 'send' },

  // ============================================================
  // Phone / Voice
  // ============================================================
  viewCalls: { domain: 'phone', action: 'read' },
  makeCall: { domain: 'phone', action: 'call' },

  // ============================================================
  // Email
  // ============================================================
  viewEmails: { domain: 'email', action: 'read' },
  sendEmail: { domain: 'email', action: 'send' },

  // ============================================================
  // Automations / Workflows
  // ============================================================
  viewAutomations: { domain: 'automations', action: 'read' },
  createAutomation: { domain: 'automations', action: 'write' },
  editAutomation: { domain: 'automations', action: 'write' },
  deleteAutomation: { domain: 'automations', action: 'delete' },
  runAutomation: { domain: 'automations', action: 'execute' },

  // ============================================================
  // AI Features
  // ============================================================
  useAI: { domain: 'ai', action: 'write' },
  viewAIMemory: { domain: 'ai', action: 'read' },
  manageAIMemory: { domain: 'ai', action: 'admin' },

  // ============================================================
  // Analytics
  // ============================================================
  viewAnalytics: { domain: 'analytics', action: 'read' },
  exportAnalytics: { domain: 'analytics', action: 'export' },

  // ============================================================
  // Team
  // ============================================================
  viewTeam: { domain: 'team', action: 'read' },
  inviteTeamMember: { domain: 'team', action: 'invite' },
  manageTeamRoles: { domain: 'team', action: 'manage' },
  removeTeamMember: { domain: 'team', action: 'remove' },

  // ============================================================
  // Settings
  // ============================================================
  viewSettings: { domain: 'settings', action: 'read' },
  updateSettings: { domain: 'settings', action: 'write' },

  // ============================================================
  // Integrations
  // ============================================================
  viewIntegrations: { domain: 'integrations', action: 'read' },
  manageIntegrations: { domain: 'integrations', action: 'manage' },

  // ============================================================
  // Billing (server-side enforced, UI uses workspace permissions)
  // ============================================================
  viewBilling: { domain: 'billing', action: 'read' },
  manageBilling: { domain: 'billing', action: 'manage' },

  // ============================================================
  // Audit
  // ============================================================
  viewAuditLog: { domain: 'audit', action: 'read' },

  // ============================================================
  // Launch Check / System Health (admin only)
  // ============================================================
  viewLaunchCheck: { domain: 'system', action: 'admin' },
  viewSystemHealth: { domain: 'system', action: 'admin' },
} as const;

/**
 * Type for all feature keys
 */
export type FeatureKey = keyof typeof FEATURE_CAPABILITIES;

/**
 * Helper to get capability requirement for a feature
 */
export function getFeatureCapability(feature: FeatureKey): CapabilityRequirement {
  return FEATURE_CAPABILITIES[feature];
}

export default FEATURE_CAPABILITIES;
